import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  fetchPptxTargetFields,
  previewPptxTemplateFieldImport,
  runPptxTemplateFieldImport,
} from './api';
import {
  PptxImportResult,
  PptxImportSlideStatus,
  PptxPreviewResult,
  PptxTargetField,
} from './types';

function getTargetFieldValue(field: PptxTargetField): string {
  return `${field.templateId}:${field.fieldName}`;
}

function formatExistingValue(value: unknown): string {
  if (value === undefined || value === null || value === '') return '';
  if (Array.isArray(value)) return value.map(item => String(item)).join('\n');
  return String(value);
}

function getStatusChip(status: PptxImportSlideStatus) {
  switch (status) {
    case 'ready':
      return <Chip size="small" color="success" label="Redo" />;
    case 'imported':
      return <Chip size="small" color="success" label="Importerad" />;
    case 'no_match':
      return <Chip size="small" color="warning" label="Ingen match" />;
    case 'failed':
      return <Chip size="small" color="error" label="Fel" />;
    case 'ignored':
    default:
      return <Chip size="small" variant="outlined" label="Ignorerad" />;
  }
}

const PptxTemplateFieldImportPage: React.FC = () => {
  const [targetFields, setTargetFields] = useState<PptxTargetField[]>([]);
  const [selectedTargetField, setSelectedTargetField] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PptxPreviewResult | null>(null);
  const [importResult, setImportResult] = useState<PptxImportResult | null>(null);
  const [loadingFields, setLoadingFields] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoadingFields(true);
    fetchPptxTargetFields()
      .then(fields => {
        if (!mounted) return;
        setTargetFields(fields);
        const preferred = fields.find(field => field.fieldName.toLowerCase() === 'uspar')
          || fields.find(field => field.recommended)
          || fields[0];
        if (preferred) {
          setSelectedTargetField(getTargetFieldValue(preferred));
        }
      })
      .catch((err: any) => {
        if (!mounted) return;
        setError(err?.response?.data?.message || err?.message || 'Kunde inte hämta template-fält.');
      })
      .finally(() => {
        if (mounted) setLoadingFields(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const readyRows = useMemo(
    () => (preview?.rows || []).filter(row => row.status === 'ready'),
    [preview],
  );

  const handlePreview = async () => {
    if (!file) {
      setError('Välj en .pptx-fil först.');
      return;
    }
    if (!selectedTargetField) {
      setError('Välj ett template-fält först.');
      return;
    }

    setProcessing(true);
    setError(null);
    setImportResult(null);
    try {
      const result = await previewPptxTemplateFieldImport(file, selectedTargetField);
      setPreview(result);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Kunde inte förhandsgranska importen.');
    } finally {
      setProcessing(false);
    }
  };

  const handleImport = async () => {
    if (!file || !selectedTargetField) return;

    setProcessing(true);
    setError(null);
    try {
      const result = await runPptxTemplateFieldImport(file, selectedTargetField);
      setImportResult(result);
      setPreview({
        targetField: result.targetField,
        rows: result.rows,
        summary: {
          totalSlides: result.summary.totalSlides,
          ready: 0,
          ignored: result.summary.ignored,
          noMatch: result.summary.noMatch,
        },
      });
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Kunde inte importera filen.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1 }}>
        PowerPoint-import till template-fält
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Läs in punktlistor från .pptx-slides, matcha produkterna via artikelnummer och skriv resultatet till valt template-fält.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFileIcon />}
          >
            Välj PowerPoint
            <input
              type="file"
              hidden
              accept=".pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation"
              onChange={(event) => {
                const selectedFile = event.target.files?.[0] || null;
                setFile(selectedFile);
                setPreview(null);
                setImportResult(null);
              }}
            />
          </Button>

          <Typography variant="body2" sx={{ minWidth: 220 }}>
            {file ? file.name : 'Ingen fil vald'}
          </Typography>

          <FormControl size="small" sx={{ minWidth: 320 }} disabled={loadingFields || targetFields.length === 0}>
            <InputLabel id="pptx-target-field-label">Målfält</InputLabel>
            <Select
              labelId="pptx-target-field-label"
              value={selectedTargetField}
              label="Målfält"
              onChange={(event) => {
                setSelectedTargetField(event.target.value);
                setPreview(null);
                setImportResult(null);
              }}
            >
              {targetFields.map(field => (
                <MenuItem key={getTargetFieldValue(field)} value={getTargetFieldValue(field)}>
                  {field.fieldLabel} ({field.templateName}) - {field.fieldType === 'bulletList' ? 'Punktlista' : field.fieldType}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            disabled={processing || !file || !selectedTargetField}
            onClick={handlePreview}
          >
            Förhandsgranska
          </Button>
        </Stack>

        {loadingFields && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Hämtar template-fält...</Typography>
          </Box>
        )}

        {!loadingFields && targetFields.length === 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Inga lämpliga template-fält hittades. Skapa gärna ett fält av typen Punktlista först.
          </Alert>
        )}
      </Paper>

      {preview && (
        <Paper sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ flex: 1 }}>
              Förhandsgranskning
            </Typography>
            <Chip label={`Slides: ${preview.summary.totalSlides}`} />
            <Chip color="success" label={`Redo: ${preview.summary.ready}`} />
            <Chip variant="outlined" label={`Ignorerade: ${preview.summary.ignored}`} />
            <Chip color="warning" label={`Ingen match: ${preview.summary.noMatch}`} />
            <Button
              variant="contained"
              color="success"
              disabled={processing || readyRows.length === 0}
              onClick={handleImport}
            >
              Importera {readyRows.length} rader
            </Button>
          </Stack>

          {importResult && (
            <Alert severity={importResult.summary.failed > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
              Import klar. Importerade: {importResult.summary.imported}, ignorerade: {importResult.summary.ignored}, ingen match: {importResult.summary.noMatch}, fel: {importResult.summary.failed}.
            </Alert>
          )}

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Slide</TableCell>
                  <TableCell>Artikelnummer</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Asset</TableCell>
                  <TableCell>Punkter</TableCell>
                  <TableCell>Notering</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.rows.map(row => (
                  <TableRow key={`${row.slideNumber}-${row.articleNumber || 'none'}`}>
                    <TableCell>{row.slideNumber}</TableCell>
                    <TableCell>{row.articleNumber || '-'}</TableCell>
                    <TableCell>{getStatusChip(row.status)}</TableCell>
                    <TableCell>{row.matchedAsset ? row.matchedAsset.title : '-'}</TableCell>
                    <TableCell>
                      {row.extractedBullets.length > 0 ? (
                        <Box component="ul" sx={{ pl: 2, my: 0 }}>
                          {row.extractedBullets.map((bullet, index) => (
                            <Box component="li" key={`${row.slideNumber}-bullet-${index}`}>
                              {bullet}
                            </Box>
                          ))}
                        </Box>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {row.reason || (row.willOverwrite ? `Skriver över befintligt värde: ${formatExistingValue(row.existingValue)}` : '')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default PptxTemplateFieldImportPage;
